import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import { ReactComponent as CloseIcon } from "../../Assets/iconsv2/generic/closeBtn.svg";
import { useTranslate } from '@tolgee/react';
import { AUDIO_SETTINGS, BACKGROUND_SETTINGS, VIDEO_SETTINGS, LANGUAGE_SETTINGS, VOICE_CLONING_SETTINGS, FEATURE_FLAGS } from '../../Constants/constantValues';
import { ReactComponent as AudioIcon } from "../../Assets/icons/callSettings/audioSettings.svg";
import { ReactComponent as VideoIcon } from "../../Assets/icons/callSettings/videoSettings.svg";
import { ReactComponent as BackgroundIcon } from "../../Assets/icons/callSettings/backgroundSettings.svg";
import { ReactComponent as GlobeIcon } from "../../Assets/iconsv2/sidebar/GlobeIcon.svg";
import { ReactComponent as MicIcon } from "../../Assets/iconsv2/controlButtons/MuteMicInactive.svg";
import CallAudioSettings from './CallAudioSettings';
import CallVideoSettings from './CallVideoSettings';
import BackgroundVideoSettings from './BackgroundVideoSettings';
import CallLanguageSettings from './CallLanguageSettings';
import VoiceCloningSettings from '../VoiceCloning/VoiceCloningSettings';
import { isSafari } from 'react-device-detect';

const CallOptionModal = ({ onClose,
    audioInputDevice,
    audioOutputDevice,
    videoInputDevice,
    micAllowed = true,
    cameraAllowed = true,
    isAttendee = false,
    backgroundType,
    setBackgoundType,
    handleVirtualBackground,
    setSelectedAudioInput,
    setSelectedAudioOutput,
    setSelectedVideoInput,
    onLanguageChange,
    isMobile = false,
    isConference = false,
    remoteNoiseFilterVisible = false,
    remoteNoiseFilterEnabled,
    setRemoteNoiseFilterEnabled,
}) => {
    const [settingsTopic, setSettingsTopic] = useState(LANGUAGE_SETTINGS);
    const { t } = useTranslate();
    const micSettingsVisible = !(isAttendee && !micAllowed);
    const cameraSettingsVisible = !(isAttendee && !cameraAllowed);
    const optionsList = [
        {
            icon: <GlobeIcon />,
            value: LANGUAGE_SETTINGS,
            label: t('profile.language')
        },
        {
            icon: <AudioIcon />,
            value: AUDIO_SETTINGS,
            label: t('general.audio'),
            exclude: isMobile,
        },
        {
            icon: <VideoIcon />,
            value: VIDEO_SETTINGS,
            label: t('general.video'),
            exclude: !cameraSettingsVisible,
        },
        {
            icon: <BackgroundIcon />,
            value: BACKGROUND_SETTINGS,
            label: t('callOptionModal.background'),
            exclude: isSafari || !cameraSettingsVisible
        },
        {
            icon: <MicIcon />,
            value: VOICE_CLONING_SETTINGS,
            label: t('voice_cloning.voice_preference'),
            exclude: !FEATURE_FLAGS.VOICE_CLONING_IN_CALL,
        },
    ]

    useEffect(() => {
        if (
            (settingsTopic === VIDEO_SETTINGS || settingsTopic === BACKGROUND_SETTINGS) &&
            !cameraSettingsVisible
        ) {
            setSettingsTopic(LANGUAGE_SETTINGS);
        }
        if (settingsTopic === AUDIO_SETTINGS && isMobile) {
            setSettingsTopic(LANGUAGE_SETTINGS);
        }
    }, [settingsTopic, cameraSettingsVisible]);

    const handleSelectTopic = (value) => {
        setSettingsTopic(value)
    }

    const displayOptionTopic = () => {
        let display = <></>
        switch (settingsTopic) {
             case LANGUAGE_SETTINGS:
                display = <CallLanguageSettings
                    onLanguageChange={onLanguageChange}
                    isConference={isConference}
                />
                break;
            case AUDIO_SETTINGS:
                display = <CallAudioSettings
                    audioInputDevice={audioInputDevice}
                    audioOutputDevice={audioOutputDevice}
                    setSelectedAudioInput={setSelectedAudioInput}
                    setSelectedAudioOutput={setSelectedAudioOutput}
                    micAllowed={micSettingsVisible}
                    remoteNoiseFilterVisible={remoteNoiseFilterVisible}
                    remoteNoiseFilterEnabled={remoteNoiseFilterEnabled}
                    setRemoteNoiseFilterEnabled={setRemoteNoiseFilterEnabled}
                />
                break;
            case VIDEO_SETTINGS:
                display = <CallVideoSettings
                    videoInputDevice={videoInputDevice}
                    setSelectedVideoInput={setSelectedVideoInput}
                    cameraAllowed={cameraSettingsVisible}
                />
                break;
            case BACKGROUND_SETTINGS:
                display = <BackgroundVideoSettings
                    backgroundType={backgroundType}
                    setBackgoundType={setBackgoundType}
                    handleVirtualBackground={handleVirtualBackground}
                />
                break;
            case VOICE_CLONING_SETTINGS:
                display = <VoiceCloningSettings />
                break;

            default:
                break;
        }
        return display

    }

    return (
        <Overlay onClick={onClose}>
            <CardModal $isMobile={isMobile} onClick={(e) => e.stopPropagation()}>
                <ListWrapper $isMobile={isMobile}>
                    <Header>
                        <Title>
                            {t('general.settings')}
                        </Title>
                        {isMobile && (
                            <CloseButton onClick={onClose}>
                                <CloseIcon />
                            </CloseButton>
                        )}
                    </Header>
                    <OptionsList $isMobile={isMobile}>
                        {optionsList.map((option, index) => (
                            !option.exclude &&
                            <OptionElement
                                key={index}
                                selected={option.value === settingsTopic}
                                onClick={() => handleSelectTopic(option.value)}
                            >
                                {option.icon}
                                <p>{option.label}</p>
                            </OptionElement>

                        ))}
                    </OptionsList>
                </ListWrapper>
                <SettingsContainer $isMobile={isMobile}>
                    {!isMobile && (
                        <Header>
                            <CloseButton onClick={onClose}>
                                <CloseIcon />
                            </CloseButton>
                        </Header>
                    )}
                    {displayOptionTopic()}
                </SettingsContainer>
            </CardModal>
        </Overlay>
    )
}

export default CallOptionModal

const Overlay = styled.div`
display:flex;
align-items:center;
justify-content:center;
position:fixed;
width:100%;
height:100%;
background:rgba(0,0,0,0.3);
left:0;
top:0;
z-index:1000;
`
const CardModal = styled.div`
display:flex;
background:${({ theme }) => theme.menu.background};
border:1px solid ${({ theme }) => theme.sidebar.borderColor};
overflow:hidden;

${({ $isMobile }) => $isMobile ? css`
  flex-direction:column;
  width:100%;
  height:100%;
  max-width:100%;
  max-height:100%;
  border-radius:0;
` : css`
  flex-direction:row;
  width:700px;
  height:600px;
  max-width:90vw;
  max-height:90vh;
  border-radius:5px;
`}
`
const ListWrapper = styled.div`
display:flex;
flex-direction:column;
width:100%;

${({ $isMobile }) => $isMobile ? css`
  height:auto;
  max-height:240px;
  flex-shrink:0;
  border-bottom:1px solid ${({ theme }) => theme.sidebar.borderColor};
` : css`
  height:100%;
  max-width:200px;
  border-right:1px solid ${({ theme }) => theme.sidebar.borderColor};
`}
`
const Header = styled.div`
display:flex;
width:100%;
padding:10px 20px;
min-height:60px;
align-items:center;
`
const SettingsContainer = styled.div`
display:flex;
flex-direction:column;
width:100%;
overflow:auto;

${({ $isMobile }) => $isMobile ? css`
  flex:1;
  min-height:0;
` : css`
  height:100%;
`}
`
const Title = styled.h3`
font-size:18px;
font-weight:500;
color:${({ theme }) => theme.menu.primaryTextColor};
`
const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  margin-left:auto;
  svg{
height:28px;
width:28px;
color:${({ theme }) => theme.menu.selectedOpionBackground};
stroke:${({ theme }) => theme.sidebar.borderColor};
}
`;

const OptionsList = styled.div`
display:flex;
flex-direction:column;
width:100%;
height:100%;
overflow:auto;
${({ $isMobile }) => $isMobile && css`
  padding-bottom:12px;
`}
`
const OptionElement = styled.div`
display:flex;
align-items:center;
gap:10px;
padding:10px;
cursor:pointer;
p{
font-size:14px;
line-height:16px;
font-size:500;
color:${({ theme }) => theme.menu.primaryTextColor};
}
svg{
height:20px;
width:20px;
color:${({ theme }) => theme.menu.primaryTextColor};
}
${({ theme, selected }) => selected && (
        `
border-left:2px solid ${theme.menu.primaryTextColor};
padding:10px 10px 10px 8px;
background:${theme.menu.groupContentBackground};
`
    )}

`
